import {
  Cartesian3,
  Ion,
  Math as CesiumMath,
  ScreenSpaceEventType,
  Viewer,
} from 'cesium';
import { config } from './config';

/**
 * Create the single full-screen Cesium Viewer used by the whole app.
 *
 * The default Bing/Ion base imagery and terrain are disabled — RailTwin supplies
 * its own Queensland imagery and DEM terrain (see imagery/ and terrain/). Widgets
 * that don't fit an immersive twin (timeline, animation, base-layer picker) are
 * turned off.
 */
export function createViewer(): Viewer {
  if (config.ion.token) {
    Ion.defaultAccessToken = config.ion.token;
  }

  const viewer = new Viewer('cesiumContainer', {
    baseLayer: false,
    baseLayerPicker: false,
    geocoder: false,
    homeButton: false,
    sceneModePicker: false,
    navigationHelpButton: false,
    animation: false,
    timeline: false,
    fullscreenButton: false,
    infoBox: false,
    selectionIndicator: false,
    // Terrain is attached later once a provider is resolved (path A/B/Ion).
  });

  const { scene } = viewer;

  // Essential for terrain-clamped picking (measurement) and correct occlusion
  // of track structure and labels behind hills.
  scene.globe.depthTestAgainstTerrain = true;

  // Atmosphere / lighting for an immersive feel; cheap wins.
  scene.globe.enableLighting = true;
  if (scene.skyAtmosphere) scene.skyAtmosphere.show = true;
  scene.fog.enabled = true;

  // Don't let the double-click handler hijack picks meant for measurement.
  scene.screenSpaceCameraController.enableCollisionDetection = true;
  viewer.cesiumWidget.screenSpaceEventHandler.removeInputAction(
    ScreenSpaceEventType.LEFT_DOUBLE_CLICK,
  );

  return viewer;
}

/** Fly the camera to the configured SEQ home view. */
export function flyHome(viewer: Viewer, durationSeconds = 0): void {
  const { lon, lat, height } = config.home;
  viewer.camera.setView({
    destination: Cartesian3.fromDegrees(lon, lat, height),
    orientation: {
      heading: CesiumMath.toRadians(0),
      pitch: CesiumMath.toRadians(-35),
      roll: 0,
    },
  });
  if (durationSeconds > 0) {
    viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(lon, lat, height),
      duration: durationSeconds,
    });
  }
}
